import React, { Component, ErrorInfo, ReactNode } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from './Button';
import { reportComponentError } from '../../utils/errorReporting';
import { forceReloadLatest } from '../../utils/appReset';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Report error to admin console
    reportComponentError(
      error,
      errorInfo.componentStack || '',
      'ErrorBoundary'
    );
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    // Optionally reload the page or navigate to a safe route
    window.location.href = '/';
  };

  handleReloadLatest = () => {
    // A stale cached build is a common cause of crashes after a deploy.
    // Clear the service worker + caches and reload the freshest version.
    void forceReloadLatest();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided by parent
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <FiAlertTriangle className="w-6 h-6 text-red-600" />
            </div>

            <h1 className="mt-4 text-2xl font-bold text-center text-gray-900">
              Something went wrong
            </h1>

            <p className="mt-2 text-center text-gray-600">
              We're sorry for the inconvenience. An unexpected error occurred.
              If this keeps happening, you may be on an outdated version — use
              "Reload latest version" below.
            </p>

            {this.state.error && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <p className="text-sm font-medium text-gray-900">Error Details:</p>
                <p className="mt-1 text-sm text-red-600 font-mono break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            {import.meta.env.DEV && this.state.errorInfo && (
              <details className="mt-4 p-4 bg-gray-50 rounded-md">
                <summary className="text-sm font-medium text-gray-900 cursor-pointer">
                  Component Stack Trace
                </summary>
                <pre className="mt-2 text-xs text-gray-700 whitespace-pre-wrap overflow-auto max-h-48">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="mt-6 flex flex-col items-center gap-3">
              <Button onClick={this.handleReloadLatest} className="w-full">
                Reload latest version
              </Button>
              <div className="flex justify-center space-x-4">
                <Button
                  variant="secondary"
                  onClick={() => window.history.back()}
                >
                  Go Back
                </Button>
                <Button
                  variant="secondary"
                  onClick={this.handleReset}
                >
                  Return to Home
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
